/**
 * Pure float layout: 1-D packing along the ring circumference.
 *
 * World frame: ring centred at origin, radius R, angles in radians clockwise
 * from 12 o'clock, +y down (SVG). Nothing here touches the DOM or the camera —
 * the caller converts screen sizes to world units first (see `floatWorldSize`).
 *
 * 1. Cull. With `viewport` set, an item whose anchor (or pin) angle falls outside
 *    the arc, widened by one of its own angular extents, is dropped entirely.
 * 2. Pinned items are placed at their pin and become fixed obstacles. They never
 *    slide and never cluster.
 * 3. Every other item wants its anchor angle at radius `R + gap + height/2`, and
 *    occupies the angle subtended by a chord of its width at that radius:
 *    `2·asin(w / 2r)` (exact for the chord, an approximation of the real
 *    rectangular footprint — good while w ≪ r, conservative as w grows).
 * 4. Packing. Obstacles cut the circle into arcs; with none, we cut at the seam
 *    with the largest slack. Each arc is then a line: substituting out the
 *    cumulative half-extents turns "keep them apart" into "keep them ordered",
 *    so pool-adjacent-violators gives the unique L2-optimal slide (minimum sum of
 *    squared displacement), clamped into the box the bounding obstacles leave.
 * 5. Any touching run containing an item slid further than `maxSlide` clusters.
 *    The single strictly highest-priority member may stay out; the rest merge in
 *    adjacent chunks of `clusterMinSize`, each cluster sitting at its members'
 *    circular mean and taking one float's width. Clusters re-enter the packing as
 *    ordinary items and merge again while anything still overflows, so a jammed
 *    arc settles into as many small clusters as fit rather than one giant one.
 *    Every round strictly reduces the entity count, so the loop terminates.
 * 6. Determinism: everything is ordered by (angle, id), the solver is unique, and
 *    output is sorted by (angle, id). Input order never matters.
 */

import type { FloatPin } from './types';

export interface LayoutItem {
  id: string;
  /** Anchor angle on the ring (rotation-free, world frame). */
  angle: number;
  /** Float extent in WORLD units (caller divides screen size by camera.scale). */
  width: number;
  height: number;
  /** Pinned position, ring-relative polar. If set, the float is fixed. */
  pin?: FloatPin | null;
  /** Higher wins when deciding who stays out of a cluster. Default 0. */
  priority?: number;
}

export interface LayoutOptions {
  /** R */
  ringRadius: number;
  /** Radial gap from ring to a float's inner edge, world units. */
  gap: number;
  /** Max angular slide (radians) allowed from ideal before clustering. */
  maxSlide: number;
  /** Minimum members for a cluster. Default 2. */
  clusterMinSize?: number;
  /** Optional culling arc (world frame, clockwise from `angleFrom`). null = all. */
  viewport?: { angleFrom: number; angleTo: number } | null;
  /** Cluster float width in world units. Default: max member width. */
  clusterWidth?: number;
  /** Cluster float height in world units. Default: max member height. */
  clusterHeight?: number;
}

export interface PlacedFloat {
  kind: 'float';
  id: string;
  /** Point on the ring at the item's anchor angle. */
  anchor: { x: number; y: number };
  /** Float centre. */
  position: { x: number; y: number };
  /** Effective angle of the float centre. */
  angle: number;
  radius: number;
  pinned: boolean;
}

export interface PlacedCluster {
  kind: 'cluster';
  /** `'cluster:' + memberIds.join(',')`, memberIds sorted. */
  id: string;
  memberIds: string[];
  /** Ring point at the members' mean angle. */
  anchor: { x: number; y: number };
  position: { x: number; y: number };
  angle: number;
  radius: number;
}

export type Placed = PlacedFloat | PlacedCluster;

const TAU = Math.PI * 2;
const EPS = 1e-9;

/* ---------------------------------------------------------------- angles --- */

/** Normalise to [0, 2π). */
function norm(a: number): number {
  if (!Number.isFinite(a)) return 0;
  const r = a % TAU;
  return r < 0 ? r + TAU : r;
}

/** Forward (clockwise) distance from `a` to `b`, in [0, 2π). */
function fwd(a: number, b: number): number {
  return norm(b - a);
}

/** Signed shortest delta from `a` to `b`, in (-π, π]. Clockwise is positive. */
export function signedAngleDelta(a: number, b: number): number {
  const d = fwd(a, b);
  return d > Math.PI ? d - TAU : d;
}

/** Circular mean of angles; falls back to the first when the vectors cancel. */
function meanAngle(angles: number[]): number {
  let sx = 0;
  let sy = 0;
  for (const a of angles) {
    sx += Math.cos(a);
    sy += Math.sin(a);
  }
  if (Math.abs(sx) < 1e-12 && Math.abs(sy) < 1e-12) return norm(angles[0] ?? 0);
  return norm(Math.atan2(sy, sx));
}

/** World point at ring-relative polar (clockwise from 12 o'clock, +y down). */
function polar(angle: number, radius: number): { x: number; y: number } {
  return { x: radius * Math.sin(angle), y: -radius * Math.cos(angle) };
}

/**
 * Full angle subtended by a float of `width` centred at `radius`: the chord
 * approximation `2·asin(w / 2r)`, saturating at 2π.
 */
export function floatAngularExtent(width: number, radius: number): number {
  const w = Number.isFinite(width) && width > 0 ? width : 0;
  if (!(radius > 0)) return TAU;
  const s = w / (2 * radius);
  return s >= 1 ? TAU : 2 * Math.asin(s);
}

/* --------------------------------------------------------------- helpers --- */

/** World extent of a constant-screen-size float at the current camera scale. */
export function floatWorldSize(
  screenW: number,
  screenH: number,
  scale: number,
): { width: number; height: number } {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return { width: screenW / s, height: screenH / s };
}

/** `['Anna','Bo','Cy','Di','Ed']` → `"Anna, Bo +3"`. */
export function clusterLabel(titles: string[], max = 2): string {
  const list = titles.filter((t) => typeof t === 'string' && t.length > 0);
  if (list.length === 0) return '';
  const n = Math.max(0, Math.floor(max));
  if (list.length <= n) return list.join(', ');
  const shown = list.slice(0, n);
  const rest = list.length - shown.length;
  return shown.length === 0 ? `+${rest}` : `${shown.join(', ')} +${rest}`;
}

/* ------------------------------------------------------------- internals --- */

interface Entity {
  id: string;
  ideal: number; // normalised anchor / mean angle
  half: number; // half angular extent at `radius`
  radius: number;
  width: number;
  height: number;
  priority: number;
  memberIds: string[]; // sorted; length 1 for a plain float
  memberAngles: number[];
  isCluster: boolean;
}

interface Obstacle {
  id: string;
  angle: number;
  half: number;
  radiusLo: number;
  radiusHi: number;
}

function byAngleThenId(a: { ideal: number; id: string }, b: { ideal: number; id: string }): number {
  if (a.ideal !== b.ideal) return a.ideal - b.ideal;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * L2 isotonic regression (pool adjacent violators): the unique non-decreasing
 * sequence minimising Σ(y−s)².
 */
function isotonic(s: number[]): number[] {
  const n = s.length;
  const sum: number[] = [];
  const cnt: number[] = [];
  for (let i = 0; i < n; i++) {
    let bs = s[i];
    let bc = 1;
    while (sum.length > 0 && sum[sum.length - 1] / cnt[cnt.length - 1] > bs / bc) {
      bs += sum.pop()!;
      bc += cnt.pop()!;
    }
    sum.push(bs);
    cnt.push(bc);
  }
  const out: number[] = new Array(n);
  let k = 0;
  for (let b = 0; b < sum.length; b++) {
    const mean = sum[b] / cnt[b];
    for (let j = 0; j < cnt[b]; j++) out[k++] = mean;
  }
  return out;
}

/**
 * Pack one arc's worth of entities (already ordered, targets unrolled and
 * monotone) into `[lo, hi]`, an inclusive box on the *first* entity's angle
 * expressed after the half-extent substitution. `lo`/`hi` may be ±Infinity.
 */
function packLine(targets: number[], halves: number[], lo: number, hi: number): number[] {
  const n = targets.length;
  if (n === 0) return [];
  const c: number[] = new Array(n);
  c[0] = 0;
  for (let i = 1; i < n; i++) c[i] = c[i - 1] + halves[i - 1] + halves[i];
  const s = targets.map((t, i) => t - c[i]);
  const y = isotonic(s);
  if (lo > hi) {
    const mid = (lo + hi) / 2;
    return y.map((_, i) => mid + c[i]);
  }
  return y.map((v, i) => Math.min(hi, Math.max(lo, v)) + c[i]);
}

/** Contiguous runs of entities that touch, expanded around each violator. */
function violatingRuns(
  xs: number[],
  halves: number[],
  violating: boolean[],
): Array<[number, number]> {
  const n = xs.length;
  const touches = (i: number): boolean =>
    xs[i + 1] - xs[i] <= halves[i] + halves[i + 1] + 1e-7;
  const runs: Array<[number, number]> = [];
  let i = 0;
  while (i < n) {
    let end = i;
    while (end + 1 < n && touches(end)) end++;
    let hit = false;
    for (let k = i; k <= end; k++) if (violating[k]) hit = true;
    if (hit) runs.push([i, end]);
    i = end + 1;
  }
  return runs;
}

/** Split into adjacent chunks of `size`; a short tail joins the chunk before it. */
function chunkRun<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  if (out.length > 1 && out[out.length - 1].length < size) {
    const tail = out.pop()!;
    out[out.length - 1].push(...tail);
  }
  return out;
}

function mergeEntities(members: Entity[], opts: LayoutOptions): Entity {
  const ids: string[] = [];
  const angles: number[] = [];
  let width = 0;
  let height = 0;
  let priority = -Infinity;
  for (const e of members) {
    ids.push(...e.memberIds);
    angles.push(...e.memberAngles);
    width = Math.max(width, e.width);
    height = Math.max(height, e.height);
    priority = Math.max(priority, e.priority);
  }
  ids.sort();
  if (opts.clusterWidth !== undefined) width = opts.clusterWidth;
  if (opts.clusterHeight !== undefined) height = opts.clusterHeight;
  const radius = opts.ringRadius + opts.gap + height / 2;
  return {
    id: `cluster:${ids.join(',')}`,
    ideal: meanAngle(angles),
    half: floatAngularExtent(width, radius) / 2,
    radius,
    width,
    height,
    priority: priority === -Infinity ? 0 : priority,
    memberIds: ids,
    memberAngles: angles,
    isCluster: true,
  };
}

/* ------------------------------------------------------------------ main --- */

export function layoutFloats(items: LayoutItem[], opts: LayoutOptions): Placed[] {
  const R = opts.ringRadius;
  const gap = opts.gap;
  const maxSlide = Math.max(0, opts.maxSlide);
  const clusterMinSize = Math.max(2, Math.floor(opts.clusterMinSize ?? 2));

  const out: Placed[] = [];
  if (!Array.isArray(items) || items.length === 0) return out;

  /* 1. cull ------------------------------------------------------------- */
  const vp = opts.viewport ?? null;
  let span = Infinity;
  if (vp) {
    const raw = vp.angleTo - vp.angleFrom;
    span = Math.abs(raw) >= TAU - 1e-12 ? Infinity : norm(raw);
  }

  const kept: LayoutItem[] = [];
  for (const it of items) {
    if (!it || typeof it.id !== 'string') continue;
    const anchorAngle = norm(it.angle);
    if (!vp || span === Infinity) {
      kept.push(it);
      continue;
    }
    const r = it.pin ? it.pin.radius : R + gap + Math.max(0, it.height) / 2;
    const margin = floatAngularExtent(Math.max(0, it.width), r);
    const inArc = (a: number): boolean => {
      const d = fwd(vp.angleFrom - margin, a);
      return d <= span + 2 * margin + EPS;
    };
    if (inArc(anchorAngle) || (it.pin ? inArc(norm(it.pin.angle)) : false)) kept.push(it);
  }
  if (kept.length === 0) return out;

  /* 2. pinned ----------------------------------------------------------- */
  const obstacles: Obstacle[] = [];
  const mobileSeed: Entity[] = [];

  for (const it of kept) {
    const width = Math.max(0, Number.isFinite(it.width) ? it.width : 0);
    const height = Math.max(0, Number.isFinite(it.height) ? it.height : 0);
    if (it.pin && Number.isFinite(it.pin.angle) && Number.isFinite(it.pin.radius)) {
      const pa = norm(it.pin.angle);
      const pr = it.pin.radius;
      out.push({
        kind: 'float',
        id: it.id,
        anchor: polar(norm(it.angle), R),
        position: polar(pa, pr),
        angle: pa,
        radius: pr,
        pinned: true,
      });
      obstacles.push({
        id: it.id,
        angle: pa,
        half: floatAngularExtent(width, Math.abs(pr) || 1) / 2,
        radiusLo: pr - height / 2,
        radiusHi: pr + height / 2,
      });
      continue;
    }
    const radius = R + gap + height / 2;
    mobileSeed.push({
      id: it.id,
      ideal: norm(it.angle),
      half: floatAngularExtent(width, radius) / 2,
      radius,
      width,
      height,
      priority: Number.isFinite(it.priority as number) ? (it.priority as number) : 0,
      memberIds: [it.id],
      memberAngles: [norm(it.angle)],
      isCluster: false,
    });
  }

  obstacles.sort((a, b) => (a.angle !== b.angle ? a.angle - b.angle : a.id < b.id ? -1 : 1));

  /* 3-4. pack, cluster, repeat ------------------------------------------ */
  let entities = mobileSeed;
  let solvedAngles = new Map<string, number>();

  if (entities.length > 0) {
    let guard = entities.length + 8;
    for (;;) {
      const { angles, runs } = packRound(entities, obstacles, maxSlide, R, gap);
      solvedAngles = angles;
      if (runs.length === 0) break;
      if (guard-- <= 0) break;

      const next: Entity[] = [];
      const consumed = new Set<number>();
      let clusteredAny = false;
      for (const idxs of runs) {
        const members = idxs.map((i) => entities[i]);
        if (members.length < clusterMinSize) continue;
        let group = members;
        // The single strictly highest-priority member may stay out, provided the
        // remainder is still large enough to cluster.
        const top = Math.max(...members.map((m) => m.priority));
        const leaders = members.filter((m) => m.priority === top);
        if (leaders.length === 1 && members.length - 1 >= clusterMinSize) {
          group = members.filter((m) => m !== leaders[0]);
          next.push(leaders[0]);
        }
        // Merge in adjacent chunks rather than collapsing the whole run at once,
        // so a jammed arc becomes several small clusters. Whatever still does not
        // fit merges again next round, up to one cluster for the run.
        for (const chunk of chunkRun(group, clusterMinSize)) {
          next.push(mergeEntities(chunk, opts));
          clusteredAny = true;
        }
        for (const i of idxs) consumed.add(i);
      }
      if (!clusteredAny) break;
      for (let i = 0; i < entities.length; i++) if (!consumed.has(i)) next.push(entities[i]);
      entities = next.sort(byAngleThenId);
    }
  }

  /* 5. emit ------------------------------------------------------------- */
  for (const e of entities) {
    const angle = solvedAngles.get(e.id) ?? e.ideal;
    if (e.isCluster) {
      out.push({
        kind: 'cluster',
        id: e.id,
        memberIds: e.memberIds.slice(),
        anchor: polar(e.ideal, R),
        position: polar(angle, e.radius),
        angle,
        radius: e.radius,
      });
    } else {
      out.push({
        kind: 'float',
        id: e.id,
        anchor: polar(e.ideal, R),
        position: polar(angle, e.radius),
        angle,
        radius: e.radius,
        pinned: false,
      });
    }
  }

  out.sort((a, b) => (a.angle !== b.angle ? a.angle - b.angle : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

/**
 * One packing pass over the mobile entities. Returns the solved angle per entity
 * id plus the touching runs (index lists into `entitiesIn`) that must cluster.
 */
function packRound(
  entitiesIn: Entity[],
  obstaclesIn: Obstacle[],
  maxSlide: number,
  R: number,
  gap: number,
): { angles: Map<string, number>; runs: number[][] } {
  const entities = entitiesIn.slice().sort(byAngleThenId);
  const indexOf = new Map<Entity, number>();
  entitiesIn.forEach((e, i) => indexOf.set(e, i));

  const angles = new Map<string, number>();
  const runs: number[][] = [];
  const n = entities.length;
  if (n === 0) return { angles, runs };

  // Only obstacles whose radial band meets the mobile band can block the track.
  // Testing against the widest mobile band rather than each float's own is
  // conservative: it can block more than strictly necessary, never less.
  let maxH = 0;
  for (const e of entities) maxH = Math.max(maxH, e.height);
  const bandLo = R + gap;
  const bandHi = R + gap + maxH;
  let cuts: Obstacle[] = obstaclesIn.filter(
    (o) => o.radiusHi >= bandLo - EPS && o.radiusLo <= bandHi + EPS,
  );

  if (cuts.length === 0) {
    // Nothing blocks the track, so cut the circle at the seam with the most
    // slack and stand a zero-width virtual obstacle there. That turns the
    // wrap-around constraint into an ordinary box on one linear chain.
    const d: number[] = new Array(n);
    for (let i = 0; i < n - 1; i++) d[i] = entities[i + 1].ideal - entities[i].ideal;
    d[n - 1] = TAU - (entities[n - 1].ideal - entities[0].ideal);
    let cut = 0;
    let best = -Infinity;
    for (let i = 0; i < n; i++) {
      const slack = d[i] - (entities[i].half + entities[(i + 1) % n].half);
      if (slack > best + 1e-12) {
        best = slack;
        cut = i;
      }
    }
    const seam = norm(entities[cut].ideal + entities[cut].half + Math.max(0, best) / 2);
    cuts = [{ id: ' seam', angle: seam, half: 0, radiusLo: -Infinity, radiusHi: Infinity }];
  }

  cuts = cuts
    .slice()
    .sort((a, b) => (a.angle !== b.angle ? a.angle - b.angle : a.id < b.id ? -1 : 1));
  const m = cuts.length;

  // Each entity belongs to the arc containing its ideal angle: a float never
  // slides past a pinned obstacle, nor across the seam.
  const buckets: number[][] = Array.from({ length: m }, () => []);
  for (let i = 0; i < n; i++) {
    let bestJ = 0;
    let bestD = Infinity;
    for (let j = 0; j < m; j++) {
      const dj = fwd(cuts[j].angle, entities[i].ideal);
      if (dj < bestD - 1e-12) {
        bestD = dj;
        bestJ = j;
      }
    }
    buckets[bestJ].push(i);
  }

  for (let j = 0; j < m; j++) {
    const bucket = buckets[j];
    if (bucket.length === 0) continue;
    const L = cuts[j];
    const Rt = cuts[(j + 1) % m];
    const arc = m === 1 ? TAU : fwd(L.angle, Rt.angle);

    const order = bucket
      .map((i) => ({ i, off: fwd(L.angle, entities[i].ideal) }))
      .sort((a, b) =>
        a.off !== b.off ? a.off - b.off : entities[a.i].id < entities[b.i].id ? -1 : 1,
      )
      .map((o) => o.i);
    const targets = order.map((i) => L.angle + fwd(L.angle, entities[i].ideal));
    const halves = order.map((i) => entities[i].half);

    let c = 0;
    for (let k = 1; k < halves.length; k++) c += halves[k - 1] + halves[k];
    const lo = L.angle + L.half + halves[0];
    const hi = L.angle + arc - Rt.half - halves[halves.length - 1] - c;

    const xs = packLine(targets, halves, lo, hi);
    const violating = xs.map((x, k) => Math.abs(x - targets[k]) > maxSlide + 1e-9);
    for (let k = 0; k < order.length; k++) angles.set(entities[order[k]].id, norm(xs[k]));
    for (const [a, b] of violatingRuns(xs, halves, violating)) {
      const idxs: number[] = [];
      for (let k = a; k <= b; k++) idxs.push(indexOf.get(entities[order[k]])!);
      runs.push(idxs);
    }
  }

  return { angles, runs };
}
